import { ProductCategory, SpecialProductCategory } from './types';
import { isEmpty } from 'lib/javascript';
import { Dispatch, memo, SetStateAction, useState } from 'react';
import { Image, Link } from 'components/common';
import cn from 'classnames';
import { routePaths } from 'appConfig';
import { CollectionMenu } from 'lib/sellingProduct';

const getCategoryImage = (url?: string) => {
  if (!url) return '';
  return `/assets/images/${url}`;
};

export const ProductCategoryCard = memo(
  ({
    id,
    hovered,
    setHovered,
    category,
    squareOnMobile,
  }: {
    id: string;
    hovered: string | null;
    setHovered: Dispatch<SetStateAction<string | null>>;
    category: ProductCategory;
    squareOnMobile?: boolean;
  }) => {
    const image = category?.assets?.[0]?.url;
    const hasImage = Boolean(image);
    const categoryIdsQuery = category.id
      .split(',')
      .map((id) => `category_ids=${id}`)
      .join('&');

    const url =
      category?.path ?? `${routePaths.PRODUCTS}?${categoryIdsQuery}`;

    return (
      <Link to={url}>
        <div
          onMouseEnter={() => setHovered(id)}
          onMouseLeave={() => setHovered(null)}
          className={cn(
            'tw-relative tw-overflow-hidden tw-w-full tw-transition-transform tw-duration-500 tw-ease-out tw-rounded-md',
            squareOnMobile ? 'tw-aspect-square' : 'tw-aspect-video',
            hovered !== null &&
              hovered === id &&
              'tw-scale-[1.02] tw-shadow-[0_10px_30px_rgba(0,0,0,0.18),0_4px_12px_rgba(0,0,0,0.12)]',
          )}
        >
          {hasImage ? (
            <Image
              src={getCategoryImage(image)}
              layout="fill"
              objectFit="cover"
              className="tw-absolute tw-inset-0"
              priority
            />
          ) : (
            <div
              className="tw-absolute tw-inset-0"
              style={{ background: '#008b8b' }}
            />
          )}

          <div
            className="tw-absolute tw-inset-0 tw-flex tw-items-start tw-flex-col tw-justify-end tw-py-8 tw-px-8 tw-transition-opacity tw-duration-300 tw-select-none"
            tabIndex={-1}
          >
            <>
              <h3 className="tw-font-bold tw-bg-clip-text tw-text-transparent tw-bg-gradient-to-b tw-from-white tw-to-white-100 tw-text-4xl tw-text-shadow-light tw-mb-1">
                {category.name}
              </h3>
              {category.description && (
                <p className="tw-mb-0 tw-text-white tw-leading-tight tw-text-shadow">
                  {category.description}
                </p>
              )}
            </>
          </div>
        </div>
      </Link>
    );
  },
);

const getCollectionTitle = (slug: string) => {
  if (slug === 'hot-sellers') return 'Hot Sellers';
  if (slug === 'new-launches') return 'New Launches';

  return '';
};

const getCollectionImage = (slug: string) => {
  if (slug === 'hot-sellers')
    return '/assets/images/categories/hot-sellers-may-2026.jpg';
  if (slug === 'new-launches')
    return '/assets/images/categories/new-launches-aug-2026.jpg';

  return '';
};

export const ProductCollectionCard = memo(
  ({
    id,
    hovered,
    setHovered,
    collection,
    squareOnMobile,
  }: {
    id: string;
    hovered: string | null;
    setHovered: Dispatch<SetStateAction<string | null>>;
    collection: CollectionMenu;
    squareOnMobile?: boolean;
  }) => {
    const image = getCollectionImage(collection.slug);
    const hasImage = Boolean(image);
    const url = `${routePaths.PRODUCTS}?collection_ids=${collection.id}`;

    return (
      <Link to={url}>
        <div
          onMouseEnter={() => setHovered(id)}
          onMouseLeave={() => setHovered(null)}
          className={cn(
            'tw-relative tw-overflow-hidden w-full tw-transition-all tw-duration-300 tw-ease-out tw-rounded-md',
            squareOnMobile ? 'tw-aspect-square' : 'tw-aspect-video',
            hovered !== null &&
              hovered === id &&
              'tw-scale-[1.02] tw-shadow-[0_10px_30px_rgba(0,0,0,0.18),0_4px_12px_rgba(0,0,0,0.12)]',
          )}
        >
          {hasImage ? (
            <Image
              src={image!}
              layout="fill"
              objectFit="cover"
              className="tw-absolute tw-inset-0"
              priority
            />
          ) : (
            <div
              className="tw-absolute tw-inset-0"
              style={{ background: '#008b8b' }}
            />
          )}

          <div
            className="tw-absolute tw-inset-0 tw-flex tw-items-start tw-flex-col tw-justify-end tw-py-8 tw-px-8 tw-transition-opacity tw-duration-300 tw-select-none"
            tabIndex={-1}
          >
            <>
              <h3 className="tw-font-bold tw-bg-clip-text tw-text-transparent tw-bg-gradient-to-b tw-from-white tw-to-white-100 tw-text-4xl tw-text-shadow-light tw-mb-1">
                {getCollectionTitle(collection.slug)}
              </h3>
              {collection.description && (
                <p className="tw-mb-0 tw-text-white tw-leading-tight tw-text-shadow">
                  {collection.description}
                </p>
              )}
            </>
          </div>
        </div>
      </Link>
    );
  },
);

const ProductCategoryGrid = ({
  categories,
  collections,
  specialProductCategories = [],
  squareOnMobile,
}: {
  categories: ProductCategory[];
  collections: CollectionMenu[];
  specialProductCategories?: SpecialProductCategory[];
  squareOnMobile?: boolean;
}) => {
  const [hovered, setHovered] = useState<string | null>(null);
  if (isEmpty(categories) && isEmpty(collections)) {
    return <p>No Categories at the moment.</p>;
  }

  const yummyGear = categories.find(
    (category) => category.slug === 'yummy-gear',
  );
  const otherCategories = categories.filter(
    (category) => category.slug !== 'yummy-gear',
  );

  return (
    <div className="tw-grid lg:tw-grid-cols-2 tw-gap-8 w-full">
      {otherCategories.map((category) => (
        <ProductCategoryCard
          key={category.id}
          id={category.id}
          hovered={hovered}
          setHovered={setHovered}
          category={category}
          squareOnMobile={squareOnMobile}
        />
      ))}
      {collections.map((collection) => (
        <ProductCollectionCard
          key={collection.id}
          id={collection.id}
          hovered={hovered}
          setHovered={setHovered}
          collection={collection}
          squareOnMobile={squareOnMobile}
        />
      ))}
      {yummyGear && (
        <ProductCategoryCard
          key={yummyGear.id}
          id={yummyGear.id}
          hovered={hovered}
          setHovered={setHovered}
          category={yummyGear}
          squareOnMobile={squareOnMobile}
        />
      )}
      {specialProductCategories.map((category) => (
        <ProductCategoryCard
          key={category.id}
          id={category.id}
          hovered={hovered}
          setHovered={setHovered}
          category={category}
          squareOnMobile={squareOnMobile}
        />
      ))}
    </div>
  );
};

export default ProductCategoryGrid;
